/**
 * Map Component that renders the Leaflet map with the belonging basemaps
 * and DataLayers depending on the passed config.
 */

import React, { Component } from 'react';
import { Map as LeafletMap } from 'react-leaflet';
import L from 'leaflet';

import { StickyContainer, Sticky } from '../../../../core_components/Sticky';

import DataLayerFactory from '../../../../core_components/DataLayer/Utils/Factory';
import FavoriteMarkerLayer from '../../../../core_components/DataLayer/FavoriteMarkerLayer';
import TimingHandler from '../../../../core_components/TimingHandler/TimingHandlerContainer';
import VideoLayer from '../../../../core_components/DataLayer/VideoLayer';
import WxnetLayer from '../../../../core_components/DataLayer/WxnetLayer';
import LivecamLayer from '../../../../core_components/DataLayer/LivecamLayer';
import BorderLayer from '../../../../core_components/DataLayer/BorderLayer';
import FullScreenControl from '../../../../core_components/FullScreen';
import Basemap from '../../../../core_components/Basemap';
// import RecommendationPanel from '../RecommendationPanel';
import ProductFamily from '../ProductFamily';
import LegendTop from '../LegendTop';
import FullScreenMobileControl from '../../../../core_components/FullScreen/FullScreenMobileControl';

import Store from '../../redux/store';
import { toggleLoading, setLoading, openFakeFullscreen, closeFakeFullscreen, toggleFullScreen } from '../../modules/App/AppState';
import * as ProductActions from '../../modules/Product/ProductState';
import Config from '../../Config.js';
import { connect } from 'react-redux';
import { composeUrlFromUrlParams } from '../UrlUtils';

import { history } from '../../modules/App/AppContainer';

import './map.scss';
import 'leaflet/dist/leaflet.css';

class MapComponent extends Component {

  constructor(props) {
    super(props);
    this.L = null;
    this.dataLayerCache = {};
    this.getCachedDataLayer = this.getCachedDataLayer.bind(this);
    this.changeHandlerBasemap = this.changeHandlerBasemap.bind(this);
    this.overlaySettingsHandler = this.overlaySettingsHandler.bind(this);
    this.setZoom = this.setZoom.bind(this);
    this.updateUrl = this.updateUrl.bind(this);
    this.toggleLoadingIndicator = this.toggleLoadingIndicator.bind(this);
    this.toggleRadar = this.toggleRadar.bind(this);
    this.getTimeConnectedDataLayers = this.getTimeConnectedDataLayers.bind(this);
    this.isInsidePgCompositShape = this.isInsidePgCompositShape.bind(this);
    this.onOpenFakeFullscreenClick = this.onOpenFakeFullscreenClick.bind(this);
    this.onCloseFakeFullscreenClick = this.onCloseFakeFullscreenClick.bind(this);
    this.MapEventActive = null;
    this.toUrl = null;
    this.state = {
      nz: true,
      multiTimeconnectedLayerIndex: 0,
    };
  }

  componentDidMount() {
    // for doing stuff with the Leaflet Element, we put it into scope
    // to use it later on (not needed at the moment)
    // see: https://github.com/PaulLeCam/react-leaflet/blob/master/docs/Components.md#mapcontrol
    this.leafletMap = this.refs.map.leafletElement;
    this.leafletMap.attributionControl.setPrefix('');
  }

  componentWillReceiveProps(nextProps) {
    nextProps.setActiveLayer(nextProps.basemap);
    if (nextProps.id !== this.props.id) {
      this.setState({ multiTimeconnectedLayerIndex: 0 });
      this.dataLayerCache = {};
    }
  }

  shouldComponentUpdate(nextProps) {
    return !(
      this.props.maxBounds === nextProps.maxBounds &&
      this.props.title === nextProps.title &&
      this.props.id === nextProps.id &&
      this.props.basemap === nextProps.basemap &&
      this.props.defaultBasemapId === nextProps.defaultBasemapId &&
      this.props.isFullScreen === nextProps.isFullScreen &&
      this.props.favorites.filter((item, i) => {
        // check if one of the favorites visibility (hide) has changed
        if (nextProps.favorites[i] && nextProps.favorites[i].hide === item.hide) {
          return false;
        }
        return true;
      }).length
    );
  }

  componentWillUnmount() {
    this.dataLayerCache = {};
  }

  onOpenFakeFullscreenClick(currentY) {
    this.props.dispatch(openFakeFullscreen(currentY));
    window.setTimeout(() => this.refs.map.leafletElement.invalidateSize(), 250);
  }

  onCloseFakeFullscreenClick() {
    if (this.props.isFakeFullscreenOpen) {
      this.props.dispatch(closeFakeFullscreen());
    }
    if (this.props.isFullScreen) {
      this.props.dispatch(toggleFullScreen());
    }
    window.setTimeout(() => this.refs.map.leafletElement.invalidateSize(), 250);
  }

  setZoom(newZoom) {
    /* eslint-disable */
    // console.log(newZoom+' | '+this.props.minZoom+' | '+this.props.maxZoom); // eslint ignore:line
    /* eslint-enable */
    let zoom = newZoom;
    if (newZoom < this.props.minZoom) zoom = this.props.minZoom;
    if (newZoom > this.props.maxZoom) zoom = this.props.maxZoom;
    if (zoom !== newZoom) this.leafletMap.setZoom(zoom);

    this.props.setNewZoom(zoom);
  }


  getCachedDataLayer(dl, i) {
    // check if overlay is already mounted
    const dlId = `${dl.type}_${i}`;
    const { leafletMap } = this;

    if (this.dataLayerCache[dlId]) {
      return this.dataLayerCache[dlId];
    }

    this.dataLayerCache[dlId] = DataLayerFactory.getLayerByType(dl.type, {
      ...dl,
      leafletMap,
      overlaySettingsHandler: this.overlaySettingsHandler,
    });

    return this.dataLayerCache[dlId];
  }

  // returns only dataLayers with timeConnected = true on index multiTimeconnectedLayerIndex
  getTimeConnectedDataLayers() {
    const layers = this.props.dataLayers.filter(dl => (dl && dl.timeConnected ? !!dl.timeConnected : false)).map(d => ({ [d.id]: d })).reduce((prev, curr) => Object.assign(prev, curr), {});

    if (Object.keys(layers).length) {
      if (Object.keys(layers).indexOf('radar_europe') > -1) {
        const index = this.isInsidePgCompositShape() ? Object.keys(layers).indexOf('radar_europe') : Object.keys(layers).indexOf('pg_composit');
        return [layers[Object.keys(layers)[index]]];
      }
      return [layers[Object.keys(layers)[0]]];
    }
    return [];
  }

  // new settings from overlay
  overlaySettingsHandler(data) {
    if (data.bounds) {
      // zoom limits
      if ((data.bounds.min_zoomlevel !== undefined) && (data.bounds.max_zoomlevel !== undefined)) {
        // console.log('new zoom limits');
        this.props.updateZoomLimits({
          min: parseInt(data.bounds.min_zoomlevel, 10),
          max: parseInt(data.bounds.max_zoomlevel, 10),
        });
        this.setZoom(this.props.zoom);
      }

      // pan limits
      if ((data.bounds.ll !== undefined) && (data.bounds.ur !== undefined)) {
        const mb = [[data.bounds.ll[1], data.bounds.ll[0]], [data.bounds.ur[1], data.bounds.ur[0]]];
        this.props.updateMaxBounds(mb);
      }
    }


    /*
    data.bounds.ll
    data.bounds.ur
    */
  }

  changeHandlerBasemap(val) {
    this.props.setActiveLayer(val);
  }

  updateUrl() {
    // console.log(this.isInsidePgCompositShape());
    this.setState({ nz: this.leafletMap.getZoom() });
    if (this.ActiveTimeout !== null) {
      clearTimeout(this.ActiveTimeout);
      const center = this.leafletMap.getCenter();
      const zoom = this.leafletMap.getZoom();
      const geo = [center.lat.toFixed(4), center.lng.toFixed(4), zoom];
      this.toUrl = composeUrlFromUrlParams(this.props.urlParams, 'geo', geo);
      this.ActiveTimeout = setTimeout(() => {
        history.push(this.toUrl);
        this.toUrl = null;
      }, 500);
    }
  }

  toggleLoadingIndicator(value) {
    this.props.dispatch(setLoading(value));
  }

  toggleRadar() {
    this.setState({ multiTimeconnectedLayerIndex: this.state.multiTimeconnectedLayerIndex ? 0 : 1 });
  }

  isInsidePgCompositShape() {
    const pgCompositBounds = L.latLngBounds(L.latLng(46.8541934, 1.9006908), L.latLng(54.9926451, 15.8785965));
    return this.leafletMap ? !pgCompositBounds.contains(this.leafletMap.getBounds()) : false;
  }

  render() {
    if ('map' in this.refs) {
      if (this.props.isFullScreen) {
        this.refs.map.leafletElement.scrollWheelZoom.enable();
      } else {
        this.refs.map.leafletElement.scrollWheelZoom.disable();
      }
    }

    const { props } = this;

    // const basemap = <Basemap basemap={props.basemap} basemaps={props.basemaps} />;
    // datalayer that are not time connectec
    const dataLayer = props.dataLayers.filter(dl => !(!!dl.timeConnected)).map(this.getCachedDataLayer);
    const timeConnectedDataLayer = this.getTimeConnectedDataLayers();

    if (props.urlParams.optional && props.urlParams.optional.indexOf('favorites') !== -1) {
      this.showFavoritesOnMap = favs => this.props.dispatch(ProductActions.showFavoritesOnMap(favs));
    }

    // create (relative) zoom class name
    const zs = ['s', 'm', 'l', 'xl', 'xxl'];
    const t = parseInt(this.state.nz / 2, 10) - 1;
    const className = 'mt-map-zoom mt-map-zoom-'.concat(this.state.nz).concat(' mt-map-zoom-').concat(zs[t]);

    return (
      <StickyContainer>
        <div>
          {this.props.isFakeFullscreenOpen || this.props.isFullScreen ?
            <div className="fullscreen-header mt-menu-bar">
              <div className="mt-menu-title float--left delta palm-gamma left">{this.props.headline}</div>
              <div className="icon-close mt-fake-fullscreen-close-button cursor--pointer" onClick={this.onCloseFakeFullscreenClick} title="Schließen" />
              <div className="clearfix"></div>
            </div>
          : null}
        </div>
        {this.props.legendVisible ?
          <LegendTop
            headline={this.props.map_legend.headline}
            ranges={this.props.map_legend.ranges}
          />
        : null}
        <div className={className}>
          <LeafletMap
            ref="map"
            id="mt-map"
            className="mt-map"
            center={props.center}
            zoom={props.zoom}
            minZoom={props.minZoom}
            maxZoom={props.maxZoom}
            maxBounds={props.maxBounds}
            onzoomend={this.updateUrl}
            ondragend={this.updateUrl}
            scrollWheelZoom={false}
            inertia={false}
          >
            {dataLayer}
            {(props.urlParams.optional && props.urlParams.optional.indexOf('wetternetzwerk') !== -1) && this.leafletMap ?
              <WxnetLayer layerContainer={this.leafletMap} parameter="te" url={Config.APP_WXNET_URL} toggleLoadingIndicator={this.toggleLoadingIndicator} />
            : null}
            {(props.urlParams.optional && props.urlParams.optional.indexOf('livecams') !== -1) && this.leafletMap ?
              <LivecamLayer layerContainer={this.leafletMap} url={Config.APP_LIVECAMS_URL} toggleLoadingIndicator={this.toggleLoadingIndicator} />
            : null}
            {(props.urlParams.optional && props.urlParams.optional.indexOf('videos') !== -1) && this.leafletMap ?
              <VideoLayer layerContainer={this.leafletMap} url={Config.APP_VIDEOS_URL} toggleLoadingIndicator={this.toggleLoadingIndicator} />
            : null}
            {(props.urlParams.optional && props.urlParams.optional.indexOf('hidefavs') === -1) ?
              <FavoriteMarkerLayer favorites={props.favorites} key="favoritemarkerlayer" />
            : null}
            <BorderLayer adminId={props.urlParams.stateId || window.maptoolConfig.country} />
            {props.defaultBasemapId ?
              <Basemap
                basemaps={props.basemaps}
                currentBasemapId={props.defaultBasemapId}
                changeHandler={this.changeHandlerBasemap}
                showSwitch={false}
              />
            : null}
            <FullScreenControl dispatch={this.props.dispatch} isFullScreen={this.props.isFullScreen} toggleFullScreen={toggleFullScreen} />
          </LeafletMap>
          <div className="mt-wrapper-bottom-panel">
            {Config.DEFAULT_ISPORTABLE ?
              <div className="mt-wrapper-bottom-panel-inner">
                <div className="mt-wrapper-bottom-panel-quickmenu">
                  {props.productFamilyId && props.id ?
                    <ProductFamily
                      productFamily={props.productFamilies[props.productFamilyId]}
                      productId={props.id}
                      urlParams={props.urlParams}
                    />
                  : null}
                </div>
                <div className="mt-wrapper-bottom-panel-timeline">
                  {this.leafletMap ?
                    <TimingHandler
                      dataLayer={timeConnectedDataLayer}
                      layerContainer={this.leafletMap}
                      store={Store}
                      toggleLoadingApp={toggleLoading}
                      overlaySettingsHandler={this.overlaySettingsHandler}
                      show_date={this.props.show_date}
                      show_time={this.props.show_time}
                    />
                  : null}
                </div>
                {/* props.id ?
                  <div className="mt-wrapper-bottom-panel-recommendation">
                    <RecommendationPanel productId={props.id} />
                  </div>
                : null*/}
              </div>
            :
              <Sticky position="bottom" topOffset={150}>
                <div className="mt-wrapper-bottom-panel-inner">
                  <div className="mt-wrapper-bottom-panel-quickmenu">
                    {props.productFamilyId && props.id ?
                      <ProductFamily
                        productFamily={props.productFamilies[props.productFamilyId]}
                        productId={props.id}
                        urlParams={props.urlParams}
                      />
                    : null}
                  </div>
                  <div className="mt-wrapper-bottom-panel-timeline">
                    {this.leafletMap ?
                      <TimingHandler
                        dataLayer={timeConnectedDataLayer}
                        layerContainer={this.leafletMap}
                        store={Store}
                        toggleLoadingApp={toggleLoading}
                        overlaySettingsHandler={this.overlaySettingsHandler}
                        show_date={this.props.show_date}
                        show_time={this.props.show_time}
                      />
                    : null}
                  </div>
                  {/* props.id ?
                      <div className="mt-wrapper-bottom-panel-recommendation">
                    <RecommendationPanel productId={props.id} />
                      </div>
                  : null */}
                </div>
              </Sticky>
            }
          </div>
        </div>
        <FullScreenMobileControl isFakeFullscreenOpen={this.props.isFakeFullscreenOpen} onOpenFakeFullscreenClick={this.onOpenFakeFullscreenClick} />
      </StickyContainer>
    );
  }
}

export default connect(state => state.AppState)(MapComponent);



/** WEBPACK FOOTER **
 ** ./apps/maptool/components/Map/index.js
 **/